import React, { Fragment, PureComponent } from 'react';

import debounce from 'lodash/debounce';
import moment from 'moment';
import cs from 'classnames';
import { arrayOf, bool, func, number, shape, object } from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { markersChanged } from 'util/BusLocationsUtils';
import {
  MARKER_POPUP_TIME_FORMAT,
  SCHEDULE_CLIENT_TIME_FORMAT,
  SCHEDULE_SERVER_TIME_FORMAT,
  MARKER_TYPE,
  MAX_MARKERS_COUNT,
} from 'constants/Values';
import { createStationMarkerInfo, stationScheduleSort } from 'util/RouteDetailsUtil';
import { Grid, Box } from '@mui/material';
import IntlMessages from 'util/IntlMessages';
import { SortableHeadedList } from 'components/UnorderedList';

import { MARKER_STATES } from 'constants/States';
import { Tooltip } from '@mui/material';
import styles from './styles.module.scss';
import markerShape from '../../constants/propTypes/markerShape';
import activeRouteDetailsShape from '../../constants/propTypes/activeRouteDetailsShape';
import PopUpInfoWindowGoogleMap from './PopUpInfoWindowGoogleMap';

import {
  getMapCenter,
  getMapZoomLevel,
  GOOGLE_MAP_ELEMENT_ID,
  generateBusStationColor,
} from './helpers';

/**
 * Render google map with popup
 */
class MapPopupInfo extends PureComponent {
  static propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    markers: arrayOf(markerShape),
    routeDetails: activeRouteDetailsShape,
    busLocationHistory: arrayOf(object),
    center: shape({
      lat: number,
      lng: number,
    }),
    withSearchBox: bool,
    markersDragAllowed: bool,
    defaultMarkersView: bool,
    addMarkerOnClick: bool,
    maxMarkersCount: number, // this prop available only with addMarkerOnClick === true
    onPlaceSelected: func, // Out params: { address: string, lat: number, lng: number }
    onSelectedWayChange: func,
    onLocationHistoryToggleUpdate: func,
    onTrafficToggleUpdate: func,
    onArrowsToggleUpdate: func,
    intl: intlShape.isRequired,
    customZoomLevel: number || null,
    recenterButton: bool,
    enableTrafficToggle: bool,
    enableArrowsToggle: bool,
    enableLocationHistoryToggle: bool,
    busLocationHistoryEnabled: bool,
    trafficEnabled: bool,
    arrowsEnabled: bool,
    disableMarkersInfoWindow: bool,
    isStopEditingUseCase: bool,
    currentStopId: number, // Prop to identify the current stop ID to exclude
    templateStops: [],
  };

  static intlMessages = {
    MULTIPLE_ROUTES: 'map.marker.multipleRoutes',
    ROUTE_WITH_STOP: 'map.marker.routeWithStop',
  };

  static defaultProps = {
    addMarkerOnClick: false,
    center: null,
    defaultMarkersView: false,
    markers: [],
    busLocationHistory: [],
    busLocationHistoryEnabled: false,
    trafficEnabled: false,
    arrowsEnabled: false,
    routeDetails: {},
    markersDragAllowed: false,
    maxMarkersCount: MAX_MARKERS_COUNT,
    onPlaceSelected: () => {},
    withSearchBox: false,
    customZoomLevel: null,
    recenterButton: false,
    disableMarkersInfoWindow: false,
    currentStopId: null,
    isStopEditingUseCase: false,
  };
  static getContentFromPreviousMarker = (prevMarkers, markerId, newHasActiveDeviation) => {
    const foundMarker = prevMarkers.find(pm => pm.markerId === markerId);

    if (!foundMarker) {
      // If no previous marker exists, initial state
      return false;
    }

    // If hasActiveDeviation has changed, respect the new state
    if (foundMarker.hasActiveDeviation !== newHasActiveDeviation) {
      return newHasActiveDeviation;
    }

    // Otherwise, respect the user's previous preference
    return (foundMarker.showInfo && foundMarker.showInfo.content) || false;
  };
  static prepareBusMarkers = (markers = [], prevMarkers = [], intl, disableMarkersInfoWindow) => {
    const nextMarkers = markers.map(
      ({
        position,
        information,
        isPositionActual,
        lastLocatedTime,
        markerIcon = '',
        markerId,
      }) => ({
        position: new google.maps.LatLng(position.lat, position.lng),
        // we use false if disableMarkersInfoWindow is true
        showInfo: disableMarkersInfoWindow
          ? false
          : {
              route: !!information,
              content: MapPopupInfo.getContentFromPreviousMarker(prevMarkers, markerId),
            },
        type: MARKER_TYPE.BUS,
        // we use false if disableMarkersInfoWindow is true
        infoContent: disableMarkersInfoWindow
          ? false
          : {
              routeInformation: <b>{information}</b>,
              fullInformation: lastLocatedTime ? (
                <div>
                  <b>{intl.formatMessage({ id: 'map.marker.route' }, { route: information })}</b>
                  <br />
                  {intl.formatMessage(
                    { id: 'map.marker.lastLocationTime' },
                    { time: moment(lastLocatedTime).format(MARKER_POPUP_TIME_FORMAT) },
                  )}
                </div>
              ) : (
                <b>{intl.formatMessage({ id: 'map.marker.route' }, { route: information })}</b>
              ),
            },
        isPositionActual,
        markerIcon,
        markerId,
      }),
    );
    return nextMarkers;
  };

  static prepareStationMarkers = ({ stations = [], direction }, prevMarkers, intl) => {
    if (!stations || !stations.length) return [];
    const stationIds = [...stations].sort(stationScheduleSort).map(station => station.id);

    const stationMarkers = createStationMarkerInfo(stations, direction, stationIds);

    return stationMarkers.map(item => {
      const stopNumber = stationIds.indexOf(item.id) + 1;
      const hasDeviatedPupils = item.pupils.some(pupil => pupil.plannedStationNumber);

      const {
        id,
        name,
        state,
        hasActiveDeviation,
        arrivalTime,
        location: {
          geometry: {
            coordinates: [lat, lng],
          },
        },
        stationOrder,
      } = item;

      const isExtraStop = !stopNumber;

      return {
        hasActiveDeviation,
        position: new google.maps.LatLng(lat, lng),
        showInfo: {
          route: false,
          content:
            hasActiveDeviation ||
            (!isExtraStop &&
              MapPopupInfo.getContentFromPreviousMarker(prevMarkers, item.id, false)),
        },
        arrivalTime,
        type: isExtraStop ? MARKER_TYPE.UNPLANNED_STATION : MARKER_TYPE.PLANNED_STATION,
        infoContent: (
          <div>
            <Box component="div" className={styles.stationName} sx={{ mb: 0.5, mr: 1 }}>
              {isExtraStop ? intl.formatMessage({ id: 'labels.extraStop' }) : name}
            </Box>
            <Grid container spacing={0}>
              {item.planedArrivalTime && (
                <Grid
                  sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
                  size={{
                    xs: 6,
                    lg: 4
                  }}>
                  <div>
                    <IntlMessages id="routes.overview.details.station.expected" />
                  </div>
                  {moment(item.planedArrivalTime, SCHEDULE_SERVER_TIME_FORMAT).format(
                    SCHEDULE_CLIENT_TIME_FORMAT,
                  )}
                </Grid>
              )}
              {item.arrivalTime && (
                <Grid
                  sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
                  size={{
                    xs: 6,
                    lg: 4
                  }}>
                  <div>
                    <IntlMessages id="routes.overview.details.station.arrived" />
                  </div>
                  {moment(item.arrivalTime, SCHEDULE_SERVER_TIME_FORMAT).format(
                    SCHEDULE_CLIENT_TIME_FORMAT,
                  )}
                </Grid>
              )}
              {item.departureTime && (
                <Grid
                  sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
                  size={{
                    xs: 6,
                    lg: 4
                  }}>
                  <div>
                    <IntlMessages id="routes.overview.details.station.departure" />
                  </div>
                  {moment(item.departureTime, SCHEDULE_SERVER_TIME_FORMAT).format(
                    SCHEDULE_CLIENT_TIME_FORMAT,
                  )}
                </Grid>
              )}
            </Grid>
            <hr />
            <SortableHeadedList
              data={item.pupils}
              heading={<div style={{ fontSize: 'small' }}>Students</div>}
              containerSx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center' }}
              placeholderId="eca.heading.no-data"
              intlIds={['allUsers.lastName', 'allUsers.firstName']}
              sortPaths={['surname', 'forename']}
              listItemPrimaryFunc={pupil => (
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '80%',
                    fontWeight: 400,
                  }}
                >
                  <Box component="div" sx={{ mr: hasDeviatedPupils ? 1 : 2 }}>
                    {pupil.forename} {pupil.surname}
                  </Box>
                  <Box component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                    {hasDeviatedPupils && (
                      <Tooltip title={intl.formatMessage({ id: 'overview.details.scheduledStop' })}>
                        <Box
                          component="div"
                          sx={{ mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          style={{
                            minWidth: '20px',
                            minHeight: '18px',
                            ...(pupil.plannedStationNumber && {
                              borderRadius: '3px',
                              // fontSize: '14px',
                              // fontFamily: 'Lato',
                              // fontWeight: '400',
                              color: '#ffffff',
                              lineHeight: 1,
                              textAlign: 'center',
                              verticalAlign: 'center',
                              backgroundColor: generateBusStationColor(pupil.plannedStationStatus),
                            }),
                          }}
                        >
                          {pupil.plannedStationNumber && pupil.plannedStationNumber}
                        </Box>
                      </Tooltip>
                    )}

                    <div
                      className={cs(styles.markerStatus, {
                        [styles.boarded]: pupil.stateForMarker === MARKER_STATES.ON_BOARDED,
                        [styles.delivered]: pupil.stateForMarker === MARKER_STATES.DELIVERED,
                        [styles.missed]: pupil.stateForMarker === MARKER_STATES.MISSED,
                        [styles.absent]: pupil.stateForMarker === MARKER_STATES.ABSENT,
                        [styles.scheduled]: pupil.stateForMarker === MARKER_STATES.SCHEDULED,
                      })}
                    >
                      {intl.formatMessage({ id: pupil.stateForMarker })}
                    </div>
                  </Box>
                </Box>
              )}
            />
          </div>
        ),
        label:
          (!isExtraStop && {
            text: String(stopNumber),
            fontSize: '14px',
            fontFamily: 'Lato',
            fontWeight: '600',
            color: '#ffffff',
          }) ||
          undefined,
        markerIcon: (!isExtraStop && {
          path:
            'M4 0 H26 A4 4 0 0 1 30 4 V16 A4 4 0 0 1 26 20 H4 A4 4 0 0 1 0 16 V4 A4 4 0 0 1 4 0 Z',
          fillColor: generateBusStationColor(state),
          fillOpacity: 1,
          strokeWeight: 0,
          rotation: 0,
          anchor: new google.maps.Point(15, 10),
          labelOrigin: new google.maps.Point(15, 10),
        }) || {
          url: 'assets/images/markerActualStop.png',
          scaledSize: new google.maps.Size(37, 37),
        },
        options: {
          closeBoxURL: '',
        },
        markerId: id,
        state,
        stationOrder,
      };
    });
  };

  static prepareLocationHistoryMarkers = (busLocationHistory = [], busLocationHistoryEnabled) => {
    if (!busLocationHistoryEnabled || !busLocationHistory || !busLocationHistory.length) return [];
    const nextMarkers = busLocationHistory.map(
      ({ location: { latitude, longitude }, requestTime: arrivalTime }) => ({
        position: new google.maps.LatLng(latitude, longitude),
        type: MARKER_TYPE.LOCATION_HISTORY,
        markerIcon: {
          path: google.maps.SymbolPath.CIRCLE,
          strokeWeight: 3,
          strokeColor: '#FF0000',
          strokeOpacity: 0.6,
        },
        markerId: `${arrivalTime}:${latitude},${longitude}`,
      }),
    );
    return nextMarkers;
  };

  static prepareTemplateStopMarkers = (templateStops = [], currentStopId = null, intl) => {
    if (!templateStops) return [];

    const createMarker = (stop, type, color) => {
      const [latitude, longitude] = stop.stopLocation;
      return {
        position: new google.maps.LatLng(latitude, longitude),
        type,
        markerIcon: {
          path: google.maps.SymbolPath.CIRCLE,
          strokeWeight: 2,
          strokeColor: color,
          strokeOpacity: 0.6,
          fillColor: color,
          fillOpacity: 0.2,
          scale: 7,
        },
        options: {
          closeBoxURL: '',
        },
        markerId: stop.stopId,
        routeNumber: stop.routeNumber,
        direction: stop.direction,
        stopName: stop.stopName,
        stopAddress: stop.stopAddress,
        schedule: stop.schedule,
        pupils: stop.pupils,
        showInfo: {
          route: false,
          content: false,
        },
        originalStopData: stop,
      };
    };

    // Create all markers first
    const pupilMarkers = (templateStops.pupilStops || []).map(stop =>
      createMarker(stop, MARKER_TYPE.PUPIL_STOP_TEMPLATE, '#00FF00'),
    );

    const routeMarkers = (templateStops.routeStops || []).map(stop =>
      createMarker(stop, MARKER_TYPE.ROUTE_STOP_TEMPLATE, '#0000ff'),
    );

    // Combine all template markers
    const allMarkers = [...pupilMarkers, ...routeMarkers];

    // Group markers by position to cluster overlapping markers
    const positionGroups = {};
    allMarkers.forEach(marker => {
      const posKey = `${marker.position.lat()},${marker.position.lng()}`;
      if (!positionGroups[posKey]) {
        positionGroups[posKey] = [];
      }
      positionGroups[posKey].push(marker);
    });

    // Process each group to create consolidated markers
    const processedMarkers = Object.values(positionGroups).map(group => {
      // If there's only one marker at this position, format it like a cluster
      if (group.length === 1) {
        const marker = group[0];
        if (marker.markerId === currentStopId) {
          return [];
        }
        return {
          ...marker,
          infoContent: (
            <div>
              {/* Stop Information Section */}
              <Box component="div" sx={{ mb: 1 }}>
                <div>
                  <b>
                    {intl.formatMessage({ id: 'map.marker.route' }, { route: marker.routeNumber })}
                  </b>
                  <div>{marker.stopName}</div>
                </div>
              </Box>

              {/* Pupils Section */}
              {marker.pupils &&
                marker.pupils.length > 0 && (
                  <Fragment>
                    <hr />
                    <div>
                      {marker.pupils.map((pupil, i) => (
                        <Box
                          component="div"
                          key={i}
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '80%',
                            fontWeight: 400,
                          }}
                        >
                          <Box component="div" sx={{ mr: 2 }}>
                            {pupil.forename} {pupil.surname}
                          </Box>
                        </Box>
                      ))}
                    </div>
                  </Fragment>
                )}
            </div>
          ),
          allRouteNumbers: [marker.routeNumber],
          groupedMarkers: 1,
          _originalMarkers: [marker],
        };
      }

      // Always keep the first marker as the base
      const baseMarker = group[0];

      // Collect all information from markers at this position
      const allRouteNumbers = [];
      const stopNamesByRoute = {};
      const allPupils = [];

      group.forEach(marker => {
        if (marker.routeNumber && !allRouteNumbers.includes(marker.routeNumber)) {
          allRouteNumbers.push(marker.routeNumber);
        }

        if (marker.stopName) {
          if (!stopNamesByRoute[marker.routeNumber]) {
            stopNamesByRoute[marker.routeNumber] = marker.stopName;
          }
        }

        if (marker.pupils && marker.pupils.length) {
          marker.pupils.forEach(pupil => {
            // Add route information to pupils for differentiation
            allPupils.push({
              ...pupil,
              _sourceRouteNumber: marker.routeNumber,
              _sourceStopName: marker.stopName,
            });
          });
        }
      });

      // Create the new consolidated infoContent
      const infoContent = (
        <div>
          {/* Stop Information Section */}
          <Box component="div" sx={{ mb: 1 }}>
            {allRouteNumbers.length === 1 ? (
              <div>
                <b>
                  {intl.formatMessage({ id: 'map.marker.route' }, { route: allRouteNumbers[0] })}
                </b>
                <div>{stopNamesByRoute[allRouteNumbers[0]]}</div>
              </div>
            ) : (
              <div>
                {(() => {
                  // Group routes by stop name
                  const stopGroups = {};
                  Object.entries(stopNamesByRoute).forEach(([route, stop]) => {
                    if (!stopGroups[stop]) {
                      stopGroups[stop] = [];
                    }
                    stopGroups[stop].push(route);
                  });

                  return Object.entries(stopGroups).map(([stopName, routes]) => (
                    <div key={stopName}>
                      <b>
                        {intl.formatMessage(
                          { id: MapPopupInfo.intlMessages.MULTIPLE_ROUTES },
                          { routes: routes.join(', ') },
                        )}
                      </b>
                      <div>{stopName}</div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </Box>

          {/* Pupils Section */}
          {allPupils.length > 0 && (
            <Fragment>
              <hr />
              {(() => {
                // Check if all pupils are from the same stop
                const allSameStop = allPupils.every(
                  p => p._sourceStopName === allPupils[0]._sourceStopName,
                );

                if (allSameStop) {
                  // Group pupils by name to show duplicates
                  const pupilsByName = {};
                  allPupils.forEach(pupil => {
                    const fullName = `${pupil.forename} ${pupil.surname}`;
                    if (!pupilsByName[fullName]) {
                      pupilsByName[fullName] = { pupil, routes: [] };
                    }
                    if (!pupilsByName[fullName].routes.includes(pupil._sourceRouteNumber)) {
                      pupilsByName[fullName].routes.push(pupil._sourceRouteNumber);
                    }
                  });

                  return (
                    <div>
                      {Object.entries(pupilsByName).map(([name, { pupil, routes }]) => (
                        <Box
                          component="div"
                          key={name}
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '80%',
                            fontWeight: 400,
                          }}
                        >
                          <Box component="div" sx={{ mr: 2 }}>
                            {pupil.forename} {pupil.surname}
                            {routes.length > 1 && (
                              <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                ({routes.join(', ')})
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </div>
                  );
                }
                // Group pupils by stop
                const pupilsByStop = {};
                allPupils.forEach(pupil => {
                  const stopKey = `${pupil._sourceRouteNumber}-${pupil._sourceStopName}`;
                  if (!pupilsByStop[stopKey]) {
                    pupilsByStop[stopKey] = {
                      routeNumber: pupil._sourceRouteNumber,
                      stopName: pupil._sourceStopName,
                      pupils: [],
                    };
                  }
                  pupilsByStop[stopKey].pupils.push(pupil);
                });

                return (
                  <div>
                    {(() => {
                      // Group by unique stop names
                      const stopGroups = {};
                      Object.values(pupilsByStop).forEach(({ stopName, pupils, routeNumber }) => {
                        if (!stopGroups[stopName]) {
                          stopGroups[stopName] = {
                            pupils: {},
                            routes: new Set(),
                          };
                        }
                        stopGroups[stopName].routes.add(routeNumber);
                        pupils.forEach(pupil => {
                          const pupilKey = `${pupil.forename} ${pupil.surname}`;
                          if (!stopGroups[stopName].pupils[pupilKey]) {
                            stopGroups[stopName].pupils[pupilKey] = {
                              pupil,
                              routes: new Set(),
                            };
                          }
                          stopGroups[stopName].pupils[pupilKey].routes.add(routeNumber);
                        });
                      });

                      return Object.entries(stopGroups).map(([stopName, { pupils, routes }]) => (
                        <div key={stopName}>
                          <Box
                            component="div"
                            sx={{ fontSize: '80%', fontWeight: 700, mt: 1 }}
                          >
                            {stopName}
                            <Box component="div" sx={{ color: 'text.secondary', fontSize: '80%', fontWeight: 400 }}>
                              {intl.formatMessage(
                                { id: MapPopupInfo.intlMessages.MULTIPLE_ROUTES },
                                { routes: Array.from(routes).join(', ') },
                              )}
                            </Box>
                          </Box>
                          {Object.values(pupils).map(({ pupil, routes: pupilRoutes }) => (
                            <Box
                              component="div"
                              key={`${pupil.forename}-${pupil.surname}`}
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '80%',
                                fontWeight: 400,
                              }}
                            >
                              <Box component="div" sx={{ mr: 2 }}>
                                {pupil.forename} {pupil.surname}
                                <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                  ({Array.from(pupilRoutes).join(', ')})
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                );
              })()}
            </Fragment>
          )}
        </div>
      );

      // Return the consolidated marker
      return {
        ...baseMarker,
        infoContent,
        allRouteNumbers,
        groupedMarkers: group.length,
        // Keep track of the original markers for reference if needed
        _originalMarkers: group,
      };
    });

    return processedMarkers;
  };

  // The processSelectedMarkers function has been integrated into prepareTemplateStopMarkers

  static collectMarkers(props, prevState, intl) {
    // eslint-disable-next-line no-param-reassign
    prevState = prevState || {};
    const {
      markers,
      templateStops,
      routeDetails,
      busLocationHistory,
      busLocationHistoryEnabled,
      disableMarkersInfoWindow,
      currentStopId,
      isStopEditingUseCase,
    } = props;
    const { markers: prevMarkers } = prevState;
    if (!routeDetails) return [];

    // Process template stops with clustering when needed
    const templateStopMarkers = MapPopupInfo.prepareTemplateStopMarkers(
      templateStops,
      currentStopId,
      intl,
    );

    return [
      ...MapPopupInfo.prepareStationMarkers(routeDetails, prevMarkers, intl),
      ...templateStopMarkers,
      ...MapPopupInfo.prepareBusMarkers(
        markers,
        prevMarkers,
        intl,
        disableMarkersInfoWindow || isStopEditingUseCase,
      ),
      ...MapPopupInfo.prepareLocationHistoryMarkers(busLocationHistory, busLocationHistoryEnabled),
    ];
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { intl } = nextProps;
    if (!nextProps.markers && !nextProps.center) {
      return null;
    }
    const nextMarkers = MapPopupInfo.collectMarkers(nextProps, prevState, intl);
    if (!prevState.centerLocked) {
      if (markersChanged(prevState.markers, nextMarkers)) {
        return {
          markers: nextMarkers,
        };
      }
      return null;
    }
    const center = (nextProps.center && nextProps.center.position) || getMapCenter(nextMarkers);
    const zoom = getMapZoomLevel(nextMarkers);

    return {
      center,
      zoom,
      markers: nextMarkers,
    };
  }

  static SNAP_PIXEL_THRESHOLD = 8; // Pixels distance for snapping

  constructor(props) {
    super(props);

    const { intl } = this.props;
    const allMarkers = MapPopupInfo.collectMarkers(this.props, null, intl);
    const center = getMapCenter(allMarkers);
    const zoom = getMapZoomLevel(allMarkers);

    this.state = {
      bounds: null,
      center: this.props.center || center,
      zoom,
      markers: allMarkers,
      centerLocked: true,
    };

    this.mapRefs = {
      map: undefined,
      searchBox: undefined,
      searchBoxInput: undefined,
    };

    this.geocoder = undefined;

    if (props.onPlaceSelected) {
      this.geocoder = new google.maps.Geocoder();
    }

    this.onBoundsChanged = debounce(this.onBoundsChanged, 1000);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      routeDetails: prevRouteDetails,
      busLocationHistory: prevBusLocationHistory,
    } = prevProps;
    const { routeDetails, busLocationHistory, intl } = this.props;
    if (
      (routeDetails && prevRouteDetails !== routeDetails) ||
      (busLocationHistory && prevBusLocationHistory !== busLocationHistory)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        markers: MapPopupInfo.collectMarkers(this.props, prevState, intl),
      });
    }
    if (
      (this.props.isStopEditingUseCase && prevProps.templateStops !== this.props.templateStops) ||
      prevProps.currentStopId !== this.props.currentStopId
    ) {
      // Check if we have exactly one marker
      const hasOneMarker = this.props.markers && this.props.markers.length === 1;

      if (hasOneMarker) {
        const marker = this.props.markers[0];
        const markerPosition = {
          latLng: new google.maps.LatLng(marker.position.lat, marker.position.lng),
        };

        this.onMarkerDragEnd(markerPosition);
      }
    }
  }

  onSearchBoxMounted = ref => {
    this.mapRefs.searchBox = ref;
  };
  onSearchBoxInputMounted = ref => {
    this.mapRefs.searchBoxInput = ref;
  };

  onMapMounted = ref => {
    this.mapRefs.map = ref;
  };

  onBoundsChanged = () => {
    const { map } = this.mapRefs;

    if (map) {
      this.setState({
        bounds: map.getBounds(),
        center: map.getCenter(),
      });
    }
  };

  /**
   * Calculates markers and new center point after elect of address from input dropdown.
   */
  onPlacesChanged = () => {
    const { onPlaceSelected } = this.props;
    const { center } = this.state;

    const places = this.mapRefs.searchBox.getPlaces();
    const bounds = new google.maps.LatLngBounds();

    places.forEach(place => {
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    let nextMarkers = [];
    const searchInput = this.mapRefs.searchBoxInput.value;
    const coordinates = this.parseCoordinates(searchInput);

    if (coordinates) {
      const position = new google.maps.LatLng(coordinates.lat, coordinates.lng);
      nextMarkers = [{ position, type: MARKER_TYPE.BUS }];
    } else {
      nextMarkers = places.map(place => ({
        position: place.geometry.location,
        type: MARKER_TYPE.BUS,
      }));
    }
    let nextCenter = center;

    if (nextMarkers && nextMarkers.length) {
      nextCenter = nextMarkers[0].position;

      if (onPlaceSelected && places && places.length) {
        // Check for nearby template markers
        const nearestMarker = this.findNearestTemplateMarker(nextCenter, this.mapRefs.map);

        if (nearestMarker) {
          // Snap to nearest marker position
          nextCenter = nearestMarker.position;
          onPlaceSelected({
            address: places[0].formatted_address,
            lat: nearestMarker.position.lat(),
            lng: nearestMarker.position.lng(),
            snappedToMarker: nearestMarker,
            placesResults: places,
            geocoderResults: null,
          });
        } else {
          onPlaceSelected({
            address: places[0].formatted_address,
            lat: nextMarkers[0].position.lat(),
            lng: nextMarkers[0].position.lng(),
            placesResults: places,
            geocoderResults: null,
          });
        }
      }
    }

    this.setState({
      center: nextCenter,
      markers: nextMarkers,
    });
  };

  latLngToPixel = (latLng, map) => {
    const projection = map.getProjection();
    const point = projection.fromLatLngToPoint(latLng);
    // eslint-disable-next-line no-restricted-properties
    const scale = Math.pow(2, map.getZoom());
    return {
      x: point.x * scale,
      y: point.y * scale,
    };
  };

  parseCoordinates = input => {
    // Match formats like "12.345, -67.890" or "12.345,-67.890"
    const regex = /^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/;
    const match = input.match(regex);

    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);

      if (
        !Number.isNaN(lat) &&
        !Number.isNaN(lng) &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      ) {
        return {
          lat,
          lng,
        };
      }
    }
    return null;
  };

  findNearestTemplateMarker = (point, map) => {
    const { markers } = this.state;
    let nearestMarker = null;
    let minPixelDistance = Infinity;
    const draggedPixel = this.latLngToPixel(point, map);

    markers.forEach(marker => {
      if (
        [MARKER_TYPE.ROUTE_STOP_TEMPLATE, MARKER_TYPE.PUPIL_STOP_TEMPLATE].includes(marker.type)
      ) {
        const markerPixel = this.latLngToPixel(marker.position, map);
        const pixelDistance = Math.sqrt(
          // eslint-disable-next-line no-restricted-properties
          Math.pow(draggedPixel.x - markerPixel.x, 2) + Math.pow(draggedPixel.y - markerPixel.y, 2),
        );

        if (
          pixelDistance < minPixelDistance &&
          pixelDistance <= MapPopupInfo.SNAP_PIXEL_THRESHOLD
        ) {
          minPixelDistance = pixelDistance;
          nearestMarker = marker;
        }
      }
    });

    return nearestMarker;
  };

  onMarkerDragEnd = marker => {
    const draggedPoint = new google.maps.LatLng(marker.latLng.lat(), marker.latLng.lng());

    const nearestMarker = this.findNearestTemplateMarker(draggedPoint, this.mapRefs.map);

    if (nearestMarker) {
      // Snap to nearest marker
      const snappedPoint = {
        lat: nearestMarker.position.lat(),
        lng: nearestMarker.position.lng(),
      };

      this.propagateSelectedPlace({
        ...snappedPoint,
        snappedToMarker: nearestMarker,
      });
    } else {
      // No nearby marker to snap to
      this.propagateSelectedPlace({
        lat: draggedPoint.lat(),
        lng: draggedPoint.lng(),
      });
    }
  };

  /**
   * Here we can handle any logic which should start from click on any point of map
   * @param point
   */
  onMapClick = point => {
    const { addMarkerOnClick, maxMarkersCount } = this.props;
    const { markers } = this.state;

    // Check if any markers have open info windows
    const hasOpenInfoWindow = markers.some(marker => marker.showInfo && marker.showInfo.content);

    if (hasOpenInfoWindow) {
      // Close all info windows
      this.setState({
        markers: markers.map(marker => ({
          ...marker,
          showInfo: marker.showInfo
            ? {
                ...marker.showInfo,
                content: false,
              }
            : marker.showInfo,
        })),
      });
      return;
    }

    if (addMarkerOnClick) {
      const newMarkers = markers.filter(marker => marker.type === MARKER_TYPE.BUS);

      if (newMarkers.length >= maxMarkersCount) {
        newMarkers.splice(0, newMarkers.length - maxMarkersCount + 1);
      }

      newMarkers.push({
        position: point.latLng,
        type: MARKER_TYPE.BUS,
      });

      this.propagateSelectedPlace({
        lat: point.latLng.lat(),
        lng: point.latLng.lng(),
      });

      this.setState({
        markers: [...markers.filter(marker => marker.type !== MARKER_TYPE.BUS), ...newMarkers],
      });
    }
  };

  handleRecenterClick = () => {
    const { intl } = this.props;
    const allMarkers = MapPopupInfo.collectMarkers(this.props, this.state, intl);
    const center = getMapCenter(allMarkers);
    const zoom = getMapZoomLevel(allMarkers);
    this.setState({ zoom, center, centerLocked: true, markers: allMarkers });
  };

  handleUserInteraction = () => {
    if (this.props.recenterButton) {
      this.setState({ centerLocked: false });
    }
  };

  handleCenterChanged = center => {
    if (this.props.recenterButton) {
      this.setState({ center });
    }
  };

  handleZoomChanged = zoom => {
    if (this.props.recenterButton) {
      this.setState({ zoom, centerLocked: false });
    }
  };

  handleMarkerHover = (targetMarker, over) => {
    // Only handle hover over events for template markers
    if (
      !over ||
      ![MARKER_TYPE.ROUTE_STOP_TEMPLATE, MARKER_TYPE.PUPIL_STOP_TEMPLATE].includes(
        targetMarker.type,
      )
    ) {
      return;
    }

    const { markers } = this.state;

    this.setState({
      markers: markers.map(marker => {
        // Skip non-template markers
        if (
          ![MARKER_TYPE.ROUTE_STOP_TEMPLATE, MARKER_TYPE.PUPIL_STOP_TEMPLATE].includes(marker.type)
        ) {
          return marker;
        }

        // Set content true only for target marker
        return {
          ...marker,
          showInfo: {
            ...marker.showInfo,
            content: marker.markerId === targetMarker.markerId,
          },
        };
      }),
    });
  };

  /**
   * Toggle to show InfoWindow and re-renders simple
   * @param { object } targetMarker marker
   */
  handleMarkerClick = (targetMarker, infoState) => {
    const { markers } = this.state;
    const { disableMarkersInfoWindow, isStopEditingUseCase } = this.props;

    if (disableMarkersInfoWindow || (isStopEditingUseCase && targetMarker.type === MARKER_TYPE.BUS))
      return;

    // Handle template marker clicks
    if (
      !infoState.closeClick &&
      [MARKER_TYPE.ROUTE_STOP_TEMPLATE, MARKER_TYPE.PUPIL_STOP_TEMPLATE].includes(targetMarker.type)
    ) {
      this.propagateSelectedPlace({
        lat: targetMarker.position.lat(),
        lng: targetMarker.position.lng(),
        snappedToMarker: targetMarker,
      });
      if (!infoState.infoWindowOpen) return;
    }

    // Determine the new state of the InfoWindow (open/close)
    const isInfoWindowOpen = targetMarker.showInfo && !targetMarker.showInfo.content;

    // Flag to track if deviations were updated in the prime data
    let hasDeviationsUpdated = false;

    if ([MARKER_TYPE.PLANNED_STATION, MARKER_TYPE.UNPLANNED_STATION].includes(targetMarker.type)) {
      const {
        routeDetails,
        routeDetails: { stations },
        onSelectedWayChange,
      } = this.props;

      // Update the prime data with deviation state changes
      const updatedRouteDetails = {
        ...routeDetails,
        stations: stations.map(plannedStation => {
          // Handle deviations for planned stations
          if (plannedStation.id === targetMarker.markerId) {
            const updatedStationPupils = plannedStation.stationPupils.map(pupil => {
              if (pupil.deviation && pupil.deviation.id === targetMarker.markerId) {
                hasDeviationsUpdated = true; // Track if deviations were updated
                return {
                  ...pupil,
                  deviation: {
                    ...pupil.deviation,
                    active: isInfoWindowOpen, // Sync deviation state with InfoWindow state
                  },
                };
              }
              return pupil;
            });

            return {
              ...plannedStation,
              stationPupils: updatedStationPupils,
            };
          }

          // Handle deviations for pupils who deviated to unplanned stations
          const updatedPupils = plannedStation.stationPupils.map(pupil => {
            if (pupil.deviation && pupil.deviation.id === targetMarker.markerId) {
              hasDeviationsUpdated = true; // Track if deviations were updated
              return {
                ...pupil,
                deviation: {
                  ...pupil.deviation,
                  active: isInfoWindowOpen, // Sync deviation state with InfoWindow state
                },
              };
            }
            return pupil;
          });

          return {
            ...plannedStation,
            stationPupils: updatedPupils,
          };
        }),
      };

      // If deviations were updated, update the prime data
      if (hasDeviationsUpdated) {
        onSelectedWayChange(updatedRouteDetails);
      }
    }

    // Update local state for marker's InfoWindow only if deviations were not updated
    if (!hasDeviationsUpdated) {
      this.setState({
        markers: markers.map(marker => {
          if (marker === targetMarker) {
            return {
              ...marker,
              showInfo: {
                ...marker.showInfo,
                content: isInfoWindowOpen, // Toggle InfoWindow state
              },
            };
          }
          return marker;
        }),
      });
    }
  };

  /**
   * We should call this method, when added/reselected marker and it have no address
   * @param lat
   * @param lng
   */
  propagateSelectedPlace = ({ lat, lng, snappedToMarker }) => {
    const { onPlaceSelected } = this.props;

    if (onPlaceSelected) {
      this.geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        let address;
        if (status === 'OK') {
          address = results && results.length ? results[0].formatted_address : '';
        }

        onPlaceSelected({
          address: address || '',
          lat,
          lng,
          snappedToMarker,
          geocoderResults: results,
          placesResults: null,
        });
      });
    }
  };

  /**
   * Convert markers to construction for google map
   * @returns { array } array of markers
   */

  render() {
    const {
      withSearchBox,
      markersDragAllowed,
      defaultMarkersView,
      customZoomLevel,
      onLocationHistoryToggleUpdate,
      onArrowsToggleUpdate,
      onTrafficToggleUpdate,
      enableTrafficToggle,
      enableArrowsToggle,
      enableLocationHistoryToggle,
      busLocationHistoryEnabled,
      trafficEnabled,
      arrowsEnabled,
    } = this.props;
    const { center, markers, bounds, zoom, centerLocked } = this.state;

    return (
      <PopUpInfoWindowGoogleMap
        containerElement={
          <Box
            id={GOOGLE_MAP_ELEMENT_ID}
            sx={{
              position: 'relative',
              display: 'block',
              width: '100%',
              height: '100%',
              p: 0,
              overflow: 'hidden',
              '& *, & *::before, & *::after': { boxSizing: 'content-box' },
              '&::before': { display: 'block', content: '""', pt: '42.857143%' },
            }}
          />
        }
        mapElement={
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        }
        center={center}
        markers={markers}
        onMarkerClick={this.handleMarkerClick}
        onMarkerHover={this.handleMarkerHover}
        bounds={bounds}
        onSearchBoxMounted={this.onSearchBoxMounted}
        onSearchBoxInputMounted={this.onSearchBoxInputMounted}
        onMapMounted={this.onMapMounted}
        onBoundsChanged={this.onBoundsChanged}
        onPlacesChanged={this.onPlacesChanged}
        onMarkerDragEnd={this.onMarkerDragEnd}
        onMapClick={this.onMapClick}
        withSearchBox={withSearchBox}
        markersDragAllowed={markersDragAllowed}
        defaultMarkersView={defaultMarkersView}
        zoom={customZoomLevel || zoom}
        showRecenterButton={!centerLocked}
        onUserInteraction={this.handleUserInteraction}
        onRecenterClick={this.handleRecenterClick}
        onCenterChanged={this.handleCenterChanged}
        onZoomChanged={this.handleZoomChanged}
        enableArrowsToggle={enableArrowsToggle}
        enableTrafficToggle={enableTrafficToggle}
        enableLocationHistoryToggle={enableLocationHistoryToggle}
        onLocationHistoryToggleUpdate={onLocationHistoryToggleUpdate}
        onTrafficToggleUpdate={onTrafficToggleUpdate}
        onArrowsToggleUpdate={onArrowsToggleUpdate}
        busLocationHistoryEnabled={busLocationHistoryEnabled}
        trafficVisible={trafficEnabled}
        arrowsVisible={arrowsEnabled}
      />
    );
  }
}

export default injectIntl(MapPopupInfo);
